import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCV } from '../../../contexts/CVContext';
import UploadModal from '../uploadModal/UploadModal';
import { useUserContext } from '../../../contexts/UserContext';
import { SigmaContainer, ControlsContainer, ZoomControl, FullScreenControl, useLoadGraph, useRegisterEvents, useSigma } from '@react-sigma/core';
import '@react-sigma/core/lib/style.css';
import Graph from 'graphology';
import { NodeCircleProgram } from 'sigma/rendering';

import BagIcon from '../../../assets/svgs/Bag';
import EducationIcon from '../../../assets/svgs/Education';
import EventIcon from '../../../assets/svgs/EventDB';
import NewsIcon from '../../../assets/svgs/NewsDB';
import UserIcon from '../../../assets/svgs/User';
import LightningIcon from '../../../assets/svgs/Lightning';

import styles from './GraphView.module.css';

// In dev, requests to /api/graph/* are proxied via setupProxy.js to the Graph backend
const GRAPH_API = '';
const MAIN_API = process.env.REACT_APP_API_URL || '';

const TYPES_META = {
  job: { fill: '#7c4dff', stroke: '#512da8', light: '#ede7f6', label: 'Jobs', icon: BagIcon },
  course: { fill: '#00acc1', stroke: '#006978', light: '#e0f7fa', label: 'Courses', icon: EducationIcon },
  event: { fill: '#2e7d32', stroke: '#1b5e20', light: '#e8f5e9', label: 'Events', icon: EventIcon },
  news: { fill: '#ef6c00', stroke: '#bf360c', light: '#fff3e0', label: 'News', icon: NewsIcon },
}
const TYPE_ORDER = ['job', 'course', 'event', 'news']

// SVG Path Registry for Canvas Rendering (Normalized to 24x24 or similar)
const ICON_PATHS = {
  job: [
    "M8 12V8a4 4 0 014-4v0a4 4 0 014 4v4",
    "M3.694 12.668c.145-1.741.218-2.611.792-3.14C5.06 9 5.934 9 7.681 9h8.639c1.746 0 2.62 0 3.194.528.574.528.647 1.399.792 3.14l.514 6.166c.084 1.013.126 1.52-.17 1.843-.298.323-.806.323-1.824.323H5.174c-1.017 0-1.526 0-1.823-.323-.297-.323-.255-.83-.17-1.843l.513-6.166z"
  ],
  course: [
    "M197.77 791.77l60.67-286.85c2.34-11.07-4.73-21.93-15.8-24.28s-21.93 4.73-24.28 15.8l-60.67 286.85c-2.34 11.07 4.73 21.93 15.8 24.28s21.93-4.73 24.28-15.8zm571.06-286.79l61.78 287.07c2.38 11.06 13.27 18.09 24.33 15.71s18.09-13.27 15.71-24.33l-61.78-287.07c-2.38-11.06-13.27-18.09-24.33-15.71s-18.09 13.27-15.71 24.33z",
    "M967.45 386.9L535.9 208.13c-10.61-4.4-30.57-4.44-41.21-.09L57.82 386.9l436.88 178.86c10.62 4.36 30.58 4.31 41.21-.09L967.45 386.9zM551.58 603.52c-20.61 8.53-51.79 8.6-72.41.14L24.44 417.49c-32.59-13.36-32.59-47.85.01-61.19l454.73-186.17c20.64-8.45 51.82-8.38 72.41.16l448.84 185.94c32.47 13.44 32.47 47.91 0 61.35l-448.84 185.94zm288.67 166.57c-98 57.57-209.67 88.36-325.89 88.36-116.36 0-228.16-30.87-326.25-88.56-9.75-5.74-22.3-2.48-28.04 7.27s-2.48 22.3 7.27 28.04c104.34 61.38 223.3 94.22 347.01 94.22 123.56 0 242.39-32.76 346.63-94 9.75-5.73 13.02-18.28 7.29-28.03s-18.28-13.02-28.03-7.29z",
    "M983.92 383.05v296.23c0 11.31 9.17 20.48 20.48 20.48s20.48-9.17 20.48-20.48V383.05c0-11.31-9.17-20.48-20.48-20.48s-20.48 9.17-20.48 20.48z"
  ],
  event: [
    "M3 10c0-1.89 0-2.83.59-3.41C4.17 6 5.11 6 7 6h10c1.89 0 2.83 0 3.41.59C21 7.17 21 8.11 21 10H3z",
    "M7 3v3M17 3v3"
  ],
  news: [
    "M5 10h14"
  ],
  user: [
    "M13.101 13.604c.369-.077.588-.463.405-.792-.404-.725-1.04-1.362-1.853-1.848C10.605 10.34 9.32 10 8 10c-1.32 0-2.605.339-3.652.964-.814.486-1.45 1.123-1.854 1.848-.183.33.037.715.405.792 3.365.7 6.838.7 10.202 0z",
    "M11.333 5.333a3.333 3.333 0 11-6.666 0 3.333 3.333 0 016.666 0z"
  ],
  lightning: [
    "M7.01 22.67H5.01c-.11 0-.22-.03-.32-.09-.1-.06-.18-.14-.24-.23-.06-.1-.09-.21-.1-.32 0-.11.02-.22.07-.32l3.94-7.71H4c-.12 0-.24-.03-.34-.09-.11-.06-.19-.15-.25-.25-.06-.11-.09-.23-.08-.35 0-.12.04-.24.1-.34l7.12-11.33c.06-.09.14-.17.23-.22.1-.05.21-.08.32-.08H20.05c.13 0 .26.03.37.1.12.07.21.17.26.29.06.12.07.26.05.39-.02.13-.08.25-.17.35l-6.04 6.87h3.59c.13 0 .25.04.36.11.1.07.19.17.24.29s.07.25.05.37-.08.23-.16.33L7.51 22.44c-.06.07-.14.13-.22.17-.09.04-.18.06-.28.06z"
  ],
  sparkle: [
    "M12 0l3 9 9 3-9 3-3 9-3-9-9-3 9-3z"
  ]
};

// Simplified path registry for rects
const ICON_RECTS = {
  event: [{ x: 3, y: 6, w: 18, h: 15, r: 2 }],
  news: [{ x: 5, y: 5, w: 14, h: 14, r: 3 }]
};

// Pre-create Path2D objects for performance
const PathMap = {};
Object.entries(ICON_PATHS).forEach(([k, paths]) => {
  PathMap[k] = paths.map(p => new Path2D(p));
});

const drawIcon = (ctx, type, x, y, size, color) => {
  ctx.save();

  // Icon sizes differ in viewBox. Calculate source resolution.
  let baseRes = 24;
  if (type === 'course') baseRes = 1024;
  else if (type === 'user') baseRes = 16;

  // Sigma size is radius. We want icon to be roughly 1.3x - 1.5x the radius in total width.
  const targetWidth = size * 1.4;
  const scale = targetWidth / baseRes;

  ctx.translate(x - (baseRes / 2 * scale), y - (baseRes / 2 * scale));
  ctx.scale(scale, scale);

  ctx.strokeStyle = color || '#fff';
  ctx.fillStyle = color || '#fff';
  ctx.lineWidth = type === 'course' ? 40 : 2; // Education paths are massive, need thicker stroke
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Draw Rects if any (Normalized to 24x24)
  if (ICON_RECTS[type]) {
    ICON_RECTS[type].forEach(r => {
      ctx.beginPath();
      ctx.roundRect(r.x, r.y, r.w, r.h, r.r);
      ctx.stroke();
    });
  }

  // Draw Paths
  if (PathMap[type]) {
    PathMap[type].forEach(path => {
      ctx.beginPath(); // Shield from previous paths
      if (type === 'lightning' || type === 'sparkle' || type === 'course' || type === 'user') {
        ctx.fill(path);
      } else {
        ctx.stroke(path);
      }
    });
  }

  ctx.restore();
};

const DEFAULT_FILTERS = {
  types: { job: true, course: true, event: true, news: true },
  shortlistedOnly: false,
  clustersOnly: false,
};

// Helper to determine dominant type of a cluster
const getDominantType = (item) => {
  if (item.type) return item.type; // It's an item
  // It's a cluster
  const counts = [
    { type: 'job', count: item.jobs_count || 0 },
    { type: 'course', count: item.courses_count || 0 },
    { type: 'event', count: item.events_count || 0 },
    { type: 'news', count: item.news_count || 0 }
  ];
  counts.sort((a, b) => b.count - a.count);
  return counts[0].count > 0 ? counts[0].type : 'job';
};

// ── WebGL Controller Component ──────────────────────────────────────────────
function GraphDataController({ data, viewState, userName, filters, setHoveredNode, selectedNode, setSelectedNode, user, currentClusterNode }) {
  const loadGraph = useLoadGraph();
  const registerEvents = useRegisterEvents();
  const sigma = useSigma();
  const lastLoadedRef = React.useRef(null);

  useEffect(() => {
    if (!data) return;
    const graph = new Graph({ multi: true });

    // 1. Gather all nodes (clusters or items)
    let apiNodes = [];
    if (viewState === 'CLUSTER') {
      let items = data.items || [];
      let subclusters = data.subclusters || [];

      // If we zoomed into a specific SUBCLUSTER, filter the items!
      if (currentClusterNode && currentClusterNode.parsed_subcluster_id) {
        const targetSubId = String(currentClusterNode.parsed_subcluster_id);
        items = items.filter(item => String(item.subcluster_id) === targetSubId);
        subclusters = []; // Hide other sibling subclusters when focused on a specific one
      }

      apiNodes = items.concat(subclusters);
    } else if (viewState === 'LOCAL') {
      apiNodes = data.nodes || [];
    }

    // 2. Filter nodes
    const filteredNodes = apiNodes.filter(n => {
      const isCluster = n.kind === 'cluster' || n.kind === 'subcluster';
      const type = getDominantType(n);

      // Strict Type Toggling dictates all nodes including clusters
      if (!filters.types[type]) return false;

      if (filters.shortlistedOnly) {
        if (isCluster) return false;
        if (!n.is_shortlisted) return false;
      }

      if (filters.clustersOnly) {
        if (!isCluster) return false;
      }

      return true;
    });

    const isClusterView = viewState === 'CLUSTER' && currentClusterNode;
    const centerNodeId = isClusterView ? `center-${currentClusterNode.id || currentClusterNode.cluster_id}` : '__user__';
    const centerNodeLabel = isClusterView ? (currentClusterNode.title || currentClusterNode.label) : (userName || 'You');
    const centerNodeKind = isClusterView ? 'cluster_center' : 'user';
    const centerNodeColor = isClusterView ? (TYPES_META[getDominantType(currentClusterNode)]?.stroke || '#432C5B') : '#432C5B';

    // Place the Center Node (User in OVERVIEW/LOCAL, Cluster in CLUSTER view)
    graph.addNode(centerNodeId, {
      x: 0,
      y: 0,
      size: 15,
      color: centerNodeColor, // Keep color for fallback or boundaries
      label: centerNodeLabel,
      kind: centerNodeKind,
      type: 'circle',
      rawData: isClusterView ? { ...currentClusterNode, kind: 'cluster_center' } : { kind: 'user', title: centerNodeLabel, description: user?.user?.email, company: user?.user?.role || 'Member' }
    });

    const nodeIds = new Set([centerNodeId]);

    // Draw Dotted Ring Boundary for Shortlisted Zone
    const numDots = 60;
    const boundaryRadius = 200; // Encompasses orbit 1 (90 to 170)
    for (let i = 0; i < numDots; i++) {
      const dotAngle = (i / numDots) * Math.PI * 2;
      const bId = `boundary-dot-${i}`;
      nodeIds.add(bId);
      graph.addNode(bId, {
        x: Math.cos(dotAngle) * boundaryRadius,
        y: Math.sin(dotAngle) * boundaryRadius,
        size: 2,
        color: 'rgba(124, 77, 255, 0.9)',
        label: i === 0 ? '⭐ Shortlisted Zone' : '',
        kind: 'boundary',
        type: 'circle'
      });
    }

    filteredNodes.forEach(node => {
      const type = getDominantType(node);

      let color = TYPES_META[type]?.fill || '#888';
      let title = node.title || node.label;

      // Calculate scaling based on node type
      let finalSize = 5;
      if (node.kind === 'cluster') {
        color = TYPES_META[type]?.fill || '#555';
        finalSize = Math.max(10, Math.min(15, Math.log10(node.count || 10) * 5));
        title = `🌌 ${title}`;
      } else if (node.kind === 'subcluster') {
        color = TYPES_META[type]?.fill || '#555';
        finalSize = Math.max(8, Math.min(12, Math.log10(node.count || 10) * 4));
        title = `✨ ${title}`;
      } else {
        finalSize = node.is_shortlisted ? 9 : 6;
      }

      // Gravity Math
      let pcaX = typeof node.x === 'number' ? node.x : (Math.random() - 0.5) * 1000;
      let pcaY = typeof node.y === 'number' ? node.y : (Math.random() - 0.5) * 1000;
      let angle = Math.atan2(pcaY, pcaX);
      if (isNaN(angle)) angle = Math.random() * Math.PI * 2;

      if (node.kind === 'cluster' || node.kind === 'subcluster') {
        angle += (Math.random() - 0.5) * 1.5;
      } else {
        angle += (Math.random() - 0.5) * 0.15;
      }

      // --- NEW RADIUS LOGIC ---
      let radius = 400;

      if (node.kind === 'cluster' || node.kind === 'subcluster') {
        // Pushed slightly past the outer rim items (550 - 800)
        radius = 550 + Math.random() * 250;
      } else if (node.is_shortlisted) {
        // Inner Core: Stays tight to the center (90 - 170)
        radius = 90 + Math.random() * 80;
      } else {
        // Outer Rim: Hugs the outside of the 200px boundary ring (250 - 500)
        radius = 250 + Math.random() * 250;
      }

      const gravityX = Math.cos(angle) * radius;
      const gravityY = Math.sin(angle) * radius;
      const id = String(node.id || `node-${node.cluster_id}`);

      nodeIds.add(id);

      const isCluster = node.kind === 'cluster' || node.kind === 'subcluster';

      graph.addNode(id, {
        x: gravityX,
        y: gravityY,
        size: finalSize,
        color: isCluster ? color : 'rgba(0,0,0,0)',
        label: title,
        rawData: node,
        kind: node.kind || node.type || type,
        type: 'circle'
      });

      if (isClusterView) {
        graph.addEdge(centerNodeId, id, {
          color: 'rgba(124, 77, 255, 0.08)',
          size: 0.5
        });
      }
    });

    if (viewState === 'CLUSTER') {
      (data.edges || []).forEach((e) => {
        const src = String(e.src || e.source);
        const dst = String(e.dst || e.target);
        if (nodeIds.has(src) && nodeIds.has(dst)) {
          graph.addEdge(src, dst, {
            color: 'rgba(124, 77, 255, 0.15)',
            size: Math.max(0.2, (e.weight || 0.2) * 2)
          });
        }
      });
    }

    loadGraph(graph);

    // Only reset camera when data source fundamentally changes
    const loadKey = `${viewState}-${centerNodeId}-${filteredNodes.length}`;
    if (lastLoadedRef.current !== loadKey) {
      lastLoadedRef.current = loadKey;
      setTimeout(() => {
        sigma.getCamera().animatedReset({ duration: 800 });
      }, 100);
    }
  }, [data, viewState, filters, loadGraph, sigma, currentClusterNode, user?.user?.email, user?.user?.role, userName]);

  // Seperate Effect for Highlighting Selections (does not re-load entire graph)
  useEffect(() => {
    if (!sigma) return;
    const graph = sigma.getGraph();
    const selId = selectedNode ? String(selectedNode.id || `node-${selectedNode.cluster_id}`) : null;

    graph.forEachNode((node) => {
      const isSel = (selId && node === selId);
      graph.setNodeAttribute(node, "selected", isSel);
    });
  }, [sigma, selectedNode]);

  // Handle Hovers and Selection Sync
  useEffect(() => {
    registerEvents({
      enterNode: (e) => {
        const rawData = sigma.getGraph().getNodeAttribute(e.node, 'rawData');
        setHoveredNode(rawData);
        sigma.getGraph().setNodeAttribute(e.node, "hovered", true);
      },
      leaveNode: (e) => {
        setHoveredNode(null);
        sigma.getGraph().setNodeAttribute(e.node, "hovered", false);
      },
      clickNode: (e) => {
        const rawData = sigma.getGraph().getNodeAttribute(e.node, 'rawData');
        setSelectedNode(rawData);
      }
    });
  }, [registerEvents, sigma, setHoveredNode, setSelectedNode]);

  return null;
}

// ── Memoized Sigma Container ────────────────────────────────────────────────
const MemoizedSigmaContainer = React.memo(({ data, viewState, userName, filters, setHoveredNode, selectedNode, setSelectedNode, user, currentClusterNode }) => {
  const sigmaSettings = useMemo(() => ({
    nodeProgramClasses: { circle: NodeCircleProgram },
    labelDensity: 1.0,
    labelGridCellSize: 1, // Minimize culling so icons show when zoomed out
    labelRenderedSizeThreshold: 0,
    defaultNodeColor: "rgba(0,0,0,0)",
    defaultEdgeColor: "rgba(124,77,255,0.1)",
    hideEdgesOnMove: true,
    defaultDrawNodeLabel: (context, data, settings) => {
      const size = data.size;
      const x = data.x;
      const y = data.y;

      if (data.kind === 'boundary') return;

      // Selection Ring
      if (data.hovered || data.selected) {
        context.beginPath();
        context.arc(x, y, size + 4, 0, 2 * Math.PI);
        context.strokeStyle = "#432C5B";
        context.lineWidth = 2;
        context.stroke();
      }

      const isCluster = data.kind === 'cluster' || data.kind === 'subcluster' || data.kind === 'cluster_center';
      const isUser = data.kind === 'user';

      if (isUser) {
        drawIcon(context, 'user', x, y, size, '#fff');

        // Show label only on hover/select
        if (data.hovered || data.selected) {
          context.font = `600 12px Poppins, Arial`;
          context.fillStyle = "#333";
          context.textAlign = "center";
          context.fillText(data.label, x, y + size + 12);
        }
      } else if (isCluster) {
        // White Circle background for clusters
        context.beginPath();
        context.arc(x, y, size + 2, 0, 2 * Math.PI);
        context.fillStyle = "rgba(255,255,255,0.95)";
        context.fill();

        context.beginPath();
        context.arc(x, y, size, 0, 2 * Math.PI);
        context.fillStyle = data.color || "#000";
        context.fill();

        const type = data.rawData?.type || getDominantType(data.rawData) || 'job';
        const iconType = data.kind === 'subcluster' ? 'lightning' : (data.kind === 'cluster_center' ? 'sparkle' : type);

        drawIcon(context, iconType, x, y, size * 0.7, '#fff');

        // Label for cluster only on hover
        if (data.hovered || data.selected) {
          context.font = `600 11px Poppins, Arial`;
          context.fillStyle = "#333";
          context.textAlign = "center";
          context.fillText(data.label, x, y + size + 12);
        }
      } else {
        // Individual Items
        const type = data.rawData?.type || 'job';
        drawIcon(context, type, x, y, size * 0.9, TYPES_META[type]?.fill || '#333');

        // Text label Only on hover
        if (data.hovered || data.selected) {
          context.font = `600 10px Poppins, Arial`;
          context.fillStyle = "#444";
          context.textAlign = "center";
          const labelText = data.label ? data.label.replace(/^💼 |^🎓 |^🗓️ |^📰 |^✨ |^🌌 /, '') : '';
          context.fillText(labelText, x, y + size + 10);
        }
      }
    }
  }), []); // Re-calculate only if absolutely necessary; for now, empty deps for stability

  return (
    <SigmaContainer
      className={styles.sigmaContainer}
      style={{ width: "100%", height: "100%", background: "transparent" }}
      settings={sigmaSettings}
    >
      <GraphDataController
        data={data}
        viewState={viewState}
        userName={userName}
        filters={filters}
        setHoveredNode={setHoveredNode}
        selectedNode={selectedNode}
        setSelectedNode={setSelectedNode}
        user={user}
        currentClusterNode={currentClusterNode}
      />
      <ControlsContainer position={"bottom-right"}>
        <ZoomControl />
        <FullScreenControl />
      </ControlsContainer>
    </SigmaContainer>
  );
});

// ── Main View Component ───────────────────────────────────────────────────
export default function GraphView() {
  const { user } = useUserContext();
  const { isCVUploaded } = useCV();
  const navigate = useNavigate();
  const userId = user?.user?.user_id;
  const userName = user?.user?.name || [user?.user?.first_name, user?.user?.last_name].filter(Boolean).join(' ') || 'You';

  useEffect(() => {
    if (!isCVUploaded && window.innerWidth <= 768) {
      navigate('/dashboard/upload');
    }
  }, [isCVUploaded, navigate]);

  const [showUploadModal, setShowUploadModal] = useState(!isCVUploaded && window.innerWidth > 768);

  useEffect(() => {
    if (!isCVUploaded && window.innerWidth > 768) {
      setShowUploadModal(true);
    }
  }, [isCVUploaded]);

  // View States: CLUSTER | LOCAL
  const [viewState, setViewState] = useState('LOCAL');
  const [currentClusterId, setCurrentClusterId] = useState(null);
  const [currentClusterNode, setCurrentClusterNode] = useState(null);

  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodeDetail, setSelectedNodeDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [hoveredNode, setHoveredNode] = useState(null);

  // Filters
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [showLegend, setShowLegend] = useState(window.innerWidth > 900);

  // Maintain breadcrumb trace essentially for zooming back up
  const [clusterHistory, setClusterHistory] = useState([]);

  // Fetch Full Node details when an item is selected
  useEffect(() => {
    if (!selectedNode || selectedNode.kind === 'user' || selectedNode.kind === 'cluster' || selectedNode.kind === 'subcluster' || selectedNode.kind === 'cluster_center') {
      setSelectedNodeDetail(null);
      return;
    }

    const fetchDetail = async () => {
      setLoadingDetail(true);
      try {
        const res = await fetch(`${MAIN_API}/feed/item/${selectedNode.id}`, { credentials: 'include' });
        if (res.ok) {
          const detail = await res.json();
          setSelectedNodeDetail(detail);
        }
      } catch (err) {
        console.error('[GraphView] Failed to fetch item detail:', err);
      } finally {
        setLoadingDetail(false);
      }
    };

    fetchDetail();
  }, [selectedNode]);

  const fetchGraphData = useCallback(async (view, clusterId = null, clusterDataNode = null) => {
    if (!userId) { setError('User not loaded.'); return; }
    setLoading(true); setError(null); setSelectedNode(null);

    // Abort after 15 seconds to avoid infinite loading
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const fetchOpts = { credentials: 'include', signal: controller.signal };

      let params = new URLSearchParams({ user_id: userId, version: 'v2_main50' });
      let payload;


      if (view === 'CLUSTER') {
        const endpoint = `${GRAPH_API}/api/graph/cluster/${clusterId}?${params}&include_items=true`;

        const res = await fetch(endpoint, fetchOpts);
        if (!res.ok) throw new Error(`Server ${res.status}`);
        payload = await res.json();
      } else if (view === 'LOCAL') {
        const overviewUrl = `${GRAPH_API}/api/graph/overview?${params}`;
        const subgraphUrl = `${GRAPH_API}/api/graph/subgraph?${new URLSearchParams({ user_id: userId, limit_nodes: '1000', limit_edges: '2000' })}`;


        const [overviewRes, localRes] = await Promise.all([
          fetch(overviewUrl, fetchOpts),
          fetch(subgraphUrl, fetchOpts)
        ]);

        if (!overviewRes.ok || !localRes.ok) {
          throw new Error(`Server errors: overview=${overviewRes.status}, subgraph=${localRes.status}`);
        }

        const overviewPayload = await overviewRes.json();
        const localPayload = await localRes.json();

        // Clean titles of emojis if backend sends them
        const cleanNode = (n) => ({
          ...n,
          title: n.title?.replace(/^💼 |^🎓 |^🗓️ |^📰 |^✨ |^🌌 /, ''),
          label: (n.label || n.title)?.replace(/^💼 |^🎓 |^🗓️ |^📰 |^✨ |^🌌 /, '')
        });

        payload = {
          nodes: [...(overviewPayload.nodes || []), ...(localPayload.nodes || [])].map(cleanNode),
          edges: [...(overviewPayload.edges || []), ...(localPayload.edges || [])]
        };
      }
      setGraphData(payload);
      setViewState(view);
      setCurrentClusterId(clusterId);
      if (clusterDataNode) setCurrentClusterNode(clusterDataNode);
    } catch (e) {
      if (e.name === 'AbortError') {
        console.error('[GraphView] Request timed out after 15s');
        setError('Request timed out. Is the Graph API server running on ' + GRAPH_API + '?');
      } else {
        console.error('[GraphView] Fetch error:', e);
        setError(e.message || 'Failed to fetch graph data');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [userId]);

  // Initial Load
  useEffect(() => {
    if (userId) fetchGraphData('LOCAL');
  }, [userId, fetchGraphData]);

  const handleZoomIntoCluster = (clusterNode) => {
    // ID could be 'cluster:33' or 'subcluster:33:2' or '33' or '33:2'
    const rawId = String(clusterNode.id || clusterNode.cluster_id || '').replace(/^(cluster:|subcluster:)/, '');

    // Split by colon to separate main cluster ID from subcluster ID
    const parts = rawId.split(':');
    const mainClusterId = parts[0];
    const subClusterId = parts.length > 1 ? parts[1] : null;

    // Save history so user can go back to parent cluster or top level
    if (viewState === 'CLUSTER' && currentClusterId) {
      setClusterHistory(prev => [...prev, { id: currentClusterId, node: currentClusterNode }]);
    }

    const activeNode = { ...clusterNode, parsed_subcluster_id: subClusterId };
    setCurrentClusterNode(activeNode);

    // We only pass the main integer ID to the backend fetch!
    fetchGraphData('CLUSTER', mainClusterId, activeNode);
  };

  const handleZoomBackOut = () => {
    // If we have history, pop it and go to the parent cluster!
    if (clusterHistory.length > 0) {
      const historyCopy = [...clusterHistory];
      const prev = historyCopy.pop();
      setClusterHistory(historyCopy);
      fetchGraphData('CLUSTER', prev.id, prev.node);
    } else {
      // Otherwise return entirely to top level LOCAL view
      fetchGraphData('LOCAL');
    }
  };

  return (
    <div className={styles.container}>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className={styles.headerBar}>
        <span className={styles.graphTitle}>Discovery Universe</span>
        <div className={styles.typePills}>
          {TYPE_ORDER.map(t => {
            const m = TYPES_META[t];
            const Icon = m.icon;
            return (
              <span key={t} className={styles.typePill} style={{ background: m.light, color: m.fill }}>
                <Icon width="12" height="12" color="currentColor" style={{ marginRight: '5px' }} />
                {m.label}
              </span>
            );
          })}
        </div>

        {viewState === 'CLUSTER' && (
          <button className={styles.backBtn} onClick={handleZoomBackOut}>← Back Out</button>
        )}

        <button className={`${styles.filterToggleBtn} ${showFilters ? styles.active : ''}`} onClick={() => { setShowFilters(v => !v); setShowLegend(false); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: showFilters ? '0' : '4px' }}>
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
          </svg>

        </button>

        <button className={`${styles.legendToggleBtn} ${showLegend ? styles.active : ''}`} onClick={() => { setShowLegend(v => !v); setShowFilters(false); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
          </svg>
          <span className={styles.legendToggleText}>Legend</span>
        </button>

        <button className={styles.refreshBtn} onClick={() => fetchGraphData(viewState, currentClusterId, currentClusterNode)} disabled={loading}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6"></path><path d="M1 20v-6h6"></path><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
        </button>
      </div>

      {/* ── Filter Panel — zero-height wrapper so it overlays the canvas ── */}
      {showFilters && (
        <div style={{ height: 0, flexShrink: 0, overflow: 'visible', position: 'relative', zIndex: 10 }}>
        <div className={styles.filterPanel}>
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Types</span>
            <div className={styles.typeToggles}>
              {TYPE_ORDER.map(type => {
                const m = TYPES_META[type];
                const Icon = m.icon;
                return (
                  <button key={type}
                    className={`${styles.typeToggle} ${filters.types[type] ? styles.typeActive : ''}`}
                    style={filters.types[type] ? { background: m.light, borderColor: m.fill, color: m.fill } : {}}
                    onClick={() => setFilters(f => ({ ...f, types: { ...f.types, [type]: !f.types[type] } }))}>
                    <Icon width="14" height="14" color="currentColor" style={{ marginRight: '6px' }} />
                    {m.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div className={styles.filterGroup}>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" checked={filters.shortlistedOnly}
                onChange={e => setFilters(f => ({ ...f, shortlistedOnly: e.target.checked, clustersOnly: false }))} />
              Shortlisted only
            </label>
            <label className={styles.checkboxLabel} style={{ marginLeft: '12px' }}>
              <input type="checkbox" checked={filters.clustersOnly}
                onChange={e => setFilters(f => ({ ...f, clustersOnly: e.target.checked, shortlistedOnly: false }))} />
              Clusters only
            </label>
          </div>
          <button className={styles.resetBtn} onClick={() => setFilters(DEFAULT_FILTERS)}>↺ Reset</button>
        </div>
        </div>
      )}

      {/* ── Main Layout ───────────────────────────────────────────────── */}
      <div className={styles.canvasRow}>
        <div className={styles.canvasWrap}>
          {loading && (
            <div className={styles.overlay}>
              <div className={styles.spinner} />
              <p className={styles.overlayText}>Calculating trajectories...</p>
            </div>
          )}
          {error && !loading && (
            <div className={styles.overlay}>
              <p className={styles.errorMsg}>⚠️ {error}</p>
              <button className={styles.retryBtn} onClick={() => fetchGraphData(viewState, currentClusterId, currentClusterNode)}>Retry</button>
            </div>
          )}

          {/* WebGL Sigma Container */}
          <MemoizedSigmaContainer
            data={graphData}
            viewState={viewState}
            userName={userName}
            filters={filters}
            setHoveredNode={setHoveredNode}
            selectedNode={selectedNode}
            setSelectedNode={setSelectedNode}
            user={user}
            currentClusterNode={currentClusterNode}
          />

          <div className={styles.controlsWrap}>
            <button className={styles.backBtn} onClick={handleZoomBackOut} disabled={viewState === 'LOCAL' && clusterHistory.length === 0}>
              ← Back
            </button>
            <div className={styles.zoomControls}>
              {/* @react-sigma components will be inside container */}
            </div>
          </div>

          {/* ── Legend ────────────────────────────────────────────────────── */}
          <div className={`${styles.legendPanel} ${showLegend ? styles.active : ''}`}>
            {/* Desktop Side Toggle Button */}
            <button
              className={styles.sideLegendToggle}
              onClick={() => setShowLegend(v => !v)}
              aria-label="Toggle Legend"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                {showLegend ? <polyline points="15 18 9 12 15 6"></polyline> : <polyline points="9 18 15 12 9 6"></polyline>}
              </svg>
              <span>LEGEND</span>
            </button>

            <div className={styles.legendPanelInner}>
              <ul>
                <li><span className={styles.legendIcon}><UserIcon width="16" height="16" color="#432C5B" /></span> <span className={styles.legendText}>You (Centre)</span></li>
                <li className={styles.legendCategoryHeader}>ITEMS</li>
                <li><span className={styles.legendIcon}><BagIcon width="16" height="16" color={TYPES_META.job.fill} /></span> <span className={styles.legendText} style={{ color: TYPES_META.job.fill }}>Job</span></li>
                <li><span className={styles.legendIcon}><NewsIcon width="16" height="16" color={TYPES_META.news.fill} /></span> <span className={styles.legendText} style={{ color: TYPES_META.news.fill }}>News</span></li>
                <li><span className={styles.legendIcon}><EducationIcon width="16" height="16" color={TYPES_META.course.fill} /></span> <span className={styles.legendText} style={{ color: TYPES_META.course.fill }}>Course</span></li>
                <li><span className={styles.legendIcon}><EventIcon width="16" height="16" color={TYPES_META.event.fill} /></span> <span className={styles.legendText} style={{ color: TYPES_META.event.fill }}>Event</span></li>
                <li className={styles.legendCategoryHeader}>CLUSTERS</li>
                <li><div className={styles.legendCluster} style={{ background: TYPES_META.job.fill }}><BagIcon width="12" height="12" color="#fff" /></div> <span className={styles.legendText}>Job Cluster</span></li>
                <li><div className={styles.legendCluster} style={{ background: TYPES_META.news.fill }}><NewsIcon width="12" height="12" color="#fff" /></div> <span className={styles.legendText}>News Cluster</span></li>
                <li><div className={styles.legendCluster} style={{ background: TYPES_META.course.fill }}><EducationIcon width="12" height="12" color="#fff" /></div> <span className={styles.legendText}>Course Cluster</span></li>
                <li><div className={styles.legendCluster} style={{ background: TYPES_META.event.fill }}><EventIcon width="12" height="12" color="#fff" /></div> <span className={styles.legendText}>Event Cluster</span></li>
                <li className={styles.legendCategoryHeader}>Inside Clusters</li>
                <li><span className={styles.legendIcon}><LightningIcon width="16" height="16" color="#888" /></span> <span className={styles.legendText}>Sub-Cluster</span></li>
                <li><div className={styles.legendCluster} style={{ background: '#432C5B' }}><svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M12 0l3 9 9 3-9 3-3 9-3-9-9-3 9-3z" /></svg></div> <span className={styles.legendText}>Core</span></li>
                <li className={styles.legendCategoryHeader}>STATUS</li>
                <li><div className={styles.legendHighlight}></div> <span className={styles.legendText}>Selected / Hover</span></li>
                <li><div className={styles.legendShortlisted}></div> <span className={styles.legendText}>Shortlisted Zone</span></li>
              </ul>
            </div>
          </div>
        </div>

        {/* ── Side Panel ────────────────────────────────────────────────── */}
        {selectedNode ? (
          <div className={styles.sidePanel}>
            <div className={styles.sidePanelHeader}>
              <span style={{ fontSize: 0, display: 'flex', alignItems: 'center' }}>
                {(() => {
                  if (selectedNode.kind === 'user') return <UserIcon width="24" height="24" color="#432C5B" />;
                  const type = getDominantType(selectedNode);
                  const Icon = selectedNode.kind === 'subcluster' ? LightningIcon : (TYPES_META[type]?.icon || UserIcon);
                  return <Icon width="24" height="24" color={selectedNode.kind === 'user' ? '#432C5B' : (TYPES_META[type]?.fill || '#888')} />;
                })()}
              </span>
              <span className={styles.sidePanelType} style={{ color: selectedNode.kind === 'user' ? '#432C5B' : (TYPES_META[getDominantType(selectedNode)]?.fill || '#888') }}>
                {((selectedNode.kind === 'cluster_center' ? 'Cluster Core' : selectedNode.kind) || selectedNode.type || 'unknown').toUpperCase()}
              </span>
              <button className={styles.closePanelBtn} onClick={() => setSelectedNode(null)}>✕</button>
            </div>

            {selectedNode.kind === 'user' ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '10px 0' }}>
                <div style={{ background: 'linear-gradient(135deg, #7c4dff, #512da8)', color: 'white', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px', marginBottom: '15px', boxShadow: '0 8px 16px rgba(124, 77, 255, 0.3)' }}>
                  👤
                </div>
                <h3 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 5px 0', color: '#1a1a1a' }}>{userName}</h3>
                <p style={{ color: '#666', margin: '0 0 15px 0', fontSize: '14px' }}>✉️ {user?.user?.email}</p>
                <div style={{ background: '#ede7f6', color: '#7c4dff', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  🏢 {user?.user?.role || 'Member'}
                </div>

                <div style={{ display: 'flex', gap: '15px', marginTop: '30px', width: '100%' }}>
                  <div style={{ flex: 1, background: 'rgba(124, 77, 255, 0.05)', padding: '15px', borderRadius: '12px', border: '1px solid rgba(124, 77, 255, 0.1)' }}>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#512da8' }}>{graphData?.nodes?.length || 0}</div>
                    <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>Total Nodes</div>
                  </div>
                  <div style={{ flex: 1, background: 'rgba(124, 77, 255, 0.05)', padding: '15px', borderRadius: '12px', border: '1px solid rgba(124, 77, 255, 0.1)' }}>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#512da8' }}>{graphData?.edges?.length || 0}</div>
                    <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>Connections</div>
                  </div>
                </div>

                <div style={{ marginTop: '25px', padding: '15px', background: 'linear-gradient(135deg, rgba(124, 77, 255, 0.05), rgba(0, 172, 193, 0.05))', borderRadius: '12px', fontSize: '13px', lineHeight: '1.6', color: '#444', borderLeft: '3px solid #7c4dff', textAlign: 'left' }}>
                  <strong>🌌 Center of the Universe</strong><br />
                  Nodes orbit you based on proximity algorithms. The closer an item is to the center, the more relevant it is to your professional profile.
                </div>
              </div>
            ) : (
              <>
                {loadingDetail ? (
                  <div style={{ padding: '30px 0', textAlign: 'center', color: '#888' }}>
                    <div className={styles.spinner} style={{ width: 30, height: 30, margin: '0 auto 15px' }}></div>
                    <p style={{ fontSize: 13, fontWeight: 500 }}>Loading record details...</p>
                  </div>
                ) : (
                  <>
                    <h3 className={styles.sidePanelTitle}>
                      {selectedNodeDetail ? (
                        selectedNodeDetail.job_title || selectedNodeDetail.course_title || selectedNodeDetail.news_title || selectedNodeDetail.event_title || selectedNode.label || selectedNode.title
                      ) : (selectedNode.label || selectedNode.title)}
                    </h3>

                    {/* Dynamic Subtitle (Company/Provider/Publisher/Host) */}
                    {(selectedNodeDetail?.job_company || selectedNodeDetail?.course_provider || selectedNodeDetail?.news_publisher || selectedNodeDetail?.event_host || selectedNode.company) && (
                      <p className={styles.sidePanelMeta}>
                        {selectedNodeDetail?.job_company ? `🏢 ${selectedNodeDetail.job_company}` :
                          selectedNodeDetail?.course_provider ? `🎓 ${selectedNodeDetail.course_provider}` :
                            selectedNodeDetail?.news_publisher ? `📰 ${selectedNodeDetail.news_publisher}` :
                              selectedNodeDetail?.event_host ? `🗓️ ${selectedNodeDetail.event_host}` :
                                `🏢 ${selectedNode.company}`}
                      </p>
                    )}

                    {/* Dynamic Location/Meta */}
                    {(selectedNodeDetail?.job_location || selectedNodeDetail?.event_location) && (
                      <p className={styles.sidePanelMeta}>📍 {selectedNodeDetail.job_location || selectedNodeDetail.event_location}</p>
                    )}

                    {/* Description / Summary */}
                    <div style={{ marginTop: '14px', fontSize: '13px', lineHeight: '1.6', color: '#444', maxHeight: '300px', overflowY: 'auto', paddingRight: '5px' }}>
                      {selectedNodeDetail ? (
                        selectedNodeDetail.job_description || selectedNodeDetail.course_description || selectedNodeDetail.news_summary || selectedNodeDetail.event_description || selectedNode.description
                      ) : selectedNode.description}
                    </div>

                    {/* Link Action */}
                    {(selectedNodeDetail?.job_url || selectedNodeDetail?.course_url || selectedNodeDetail?.news_url || selectedNodeDetail?.event_url) && (
                      <a href={selectedNodeDetail.job_url || selectedNodeDetail.course_url || selectedNodeDetail.news_url || selectedNodeDetail.event_url}
                        target="_blank" rel="noopener noreferrer"
                        className={styles.drillDownBtn}
                        style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '20px', gap: '8px' }}>
                        Link <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                      </a>
                    )}
                  </>
                )}

                {/* Cluster Stats */}
                {(selectedNode.kind === 'cluster' || selectedNode.kind === 'subcluster') && (
                  <div className={styles.clusterStats}>
                    <p><strong>Total Items:</strong> {selectedNode.count?.toLocaleString()}</p>
                    {selectedNode.jobs_count > 0 && <p><BagIcon width="12" height="12" color="currentColor" style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Jobs: {selectedNode.jobs_count?.toLocaleString()}</p>}
                    {selectedNode.courses_count > 0 && <p><EducationIcon width="12" height="12" color="currentColor" style={{ verticalAlign: 'middle', marginRight: '4px' }} /> Courses: {selectedNode.courses_count?.toLocaleString()}</p>}

                    <button className={styles.drillDownBtn} onClick={() => handleZoomIntoCluster(selectedNode)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                      Dive into {selectedNode.kind === 'subcluster' ? 'Sub-Cluster' : 'Cluster'}
                    </button>
                  </div>
                )}

                {/* Sub-Cluster/Center Stats */}
                {selectedNode.kind === 'cluster_center' && (
                  <div className={styles.clusterStats}>
                    <p><strong>Total Items:</strong> {selectedNode.count?.toLocaleString()}</p>
                    <p>You are viewing the interior of this cluster.</p>
                    <p>Its contents are gravitationally orbiting it based on relevance.</p>
                  </div>
                )}

                {/* Individual Item Stats */}
                {selectedNode.match_percent != null && (
                  <div className={styles.matchBadge} style={{ background: '#ede7f6' }}>
                    <span className={styles.matchNum} style={{ color: '#7c4dff' }}>{selectedNode.match_percent}%</span>
                    <span className={styles.matchLbl}>Match</span>
                  </div>
                )}
                {selectedNode.is_shortlisted && (
                  <div className={styles.shortlistedTag} style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                    Shortlisted
                  </div>
                )}

                {selectedNode.bullets?.length > 0 && (
                  <ul className={styles.bullets}>
                    {selectedNode.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                )}
              </>
            )}
          </div>
        ) : (
          <div className={styles.sidePanelEmpty}>
            <div className={styles.sidePanelEmptyIcon}>
              <LightningIcon width="40" height="40" color="#ddd" />
            </div>
            <p>Click any node or cluster for details</p>
          </div>
        )}
      </div>

      {hoveredNode && !selectedNode && (
        <div className={styles.tooltipFixed}>
          <strong>{hoveredNode.label || hoveredNode.title}</strong>
          {hoveredNode.count && <span> ({hoveredNode.count} items)</span>}
          {hoveredNode.match_percent && <span> · {hoveredNode.match_percent}% match</span>}
        </div>
      )}
      {showUploadModal && (
        <UploadModal
          isModal={true}
          onClose={() => setShowUploadModal(false)}
        />
      )}
    </div>
  );
}
